import { NotificationData } from '../../../services/notification.service';
import {
  PurchaseOption,
  WayPurchaseNotificationData,
  WayPurchaseOptionCard,
  WayShopPurchaseOption,
  WayNormalizedPurchase,
  ConfirmShopPurchaseRequest,
} from '../models/way-purchase.models';
import { buildCreateOrderRequestFromLanePrepaidRedeemPusher } from '../../../shared/utils/get-lp-image-prepaid-redeem.util';
import { pickString, readRecord } from './way-purchase-display.helpers';
import { isRedeemableWayPurchase } from './way-purchase-lookup-orchestration.util';
import { NotificationUtils } from '../../../notifications/notification.utils';

const SHOP_CONFIRM_REDEEM_EVENTS = new Set(['order-success', 'active-subscription']);

export function isShopPurchaseOption(option: PurchaseOption): option is WayShopPurchaseOption {
  return option.source === 'shop';
}

export function isWayPurchaseOption(option: PurchaseOption): option is WayPurchaseOptionCard {
  return option.source === 'way';
}

export function buildShopSelectionNotification(
  parentData: WayPurchaseNotificationData | Record<string, any> | null | undefined,
  shopOption: WayShopPurchaseOption
): NotificationData | null {
  const row = readRecord(parentData);
  if (!row) {
    return null;
  }

  const redeemEvent = pickString(shopOption.redeem_event);
  if (!redeemEvent) {
    return null;
  }

  if (redeemEvent === 'redeem-prepaid-purchase') {
    const mapped = buildCreateOrderRequestFromLanePrepaidRedeemPusher({
      ...row,
      ...shopOption,
      prepaid_purchase_id: shopOption.prepaid_purchase_id ?? null,
      event: redeemEvent,
    });
    return {
      type: mapped.type,
      message: mapped.message,
      timestamp: new Date(),
      data: mapped.data,
    };
  }

  const data: Record<string, any> = {
    ...row,
    order_id: shopOption.order_id ?? null,
    orderId: shopOption.order_id ?? null,
    subscription_id: shopOption.subscription_id ?? null,
    subscriptionId: shopOption.subscription_id ?? null,
    package_id: shopOption.package_id ?? null,
    packageId: shopOption.package_id ?? null,
    package_name: shopOption.package_name ?? null,
    customer_id: shopOption.customer_id ?? null,
    vehicle_id: shopOption.vehicle_id ?? null,
  };

  return {
    type: redeemEvent as NotificationData['type'],
    message: redeemEvent,
    timestamp: new Date(),
    data,
  };
}

export function buildWaySelectionConfirmContext(
  parentData: WayPurchaseNotificationData | Record<string, any> | null | undefined,
  wayOption: WayPurchaseOptionCard
): Record<string, any> | null {
  const row = readRecord(parentData);
  const purchase = wayOption.purchase;
  const wayPassId = pickString(purchase?.way_pass_id);
  const locationId = pickString(row?.['location_id'], row?.['locationId']);
  const lprEntryId = pickString(row?.['lpr_entry_id'], row?.['lprEntryId'], row?.['id']);
  // Prefer Way plate; fall back to attendant/lookup plate when Way has none.
  const plateNumber = pickString(purchase?.plate_number, row?.['plate'], row?.['plateNumber']);

  // Lane LPR provides lpr_entry_id; order-request phone/email lookup provides plate instead.
  if (!row || !wayPassId || !locationId || (!lprEntryId && !plateNumber)) {
    return null;
  }

  const purchaseWithPlate: WayNormalizedPurchase = {
    ...(purchase as WayNormalizedPurchase),
    ...(plateNumber ? { plate_number: plateNumber } : {}),
  };

  return {
    ...row,
    purchase: purchaseWithPlate,
    local_package_id: pickString(wayOption.local_package_id, purchase?.local_package_id) || null,
  };
}

export function buildConfirmShopPurchaseRequest(
  parentData: WayPurchaseNotificationData | Record<string, any> | null | undefined,
  shopOption: WayShopPurchaseOption
): ConfirmShopPurchaseRequest | null {
  const row = readRecord(parentData);
  const redeemEvent = pickString(shopOption.redeem_event);
  const orderId = pickString(shopOption.order_id);
  const lprEntryId = pickString(row?.['lpr_entry_id'], row?.['lprEntryId'], row?.['id']);
  const locationId = pickString(row?.['location_id'], row?.['locationId']);
  const roiId = pickString(row?.['roi_id'], row?.['roiID'], row?.['roiId']);

  if (!row || !redeemEvent || !SHOP_CONFIRM_REDEEM_EVENTS.has(redeemEvent)) {
    return null;
  }
  if (!orderId || !lprEntryId || !locationId) {
    return null;
  }

  const subscriptionId = pickString(shopOption.subscription_id);
  return {
    lpr_entry_id: lprEntryId,
    order_id: orderId,
    location_id: locationId,
    redeem_event: redeemEvent as ConfirmShopPurchaseRequest['redeem_event'],
    ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
    ...(roiId ? { roi_id: roiId } : {}),
  };
}

export function buildNotificationFromConfirmShopPurchaseResponse(
  response: any,
  redeemEvent: ConfirmShopPurchaseRequest['redeem_event']
): NotificationData | null {
  const envelope = readRecord(response);
  const data = readRecord(envelope?.['data']);
  if (!data) {
    return null;
  }

  const message =
    pickString(data['message'], envelope?.['message']) ||
    NotificationUtils.getMessageForEventType(redeemEvent);

  return {
    type: redeemEvent,
    message,
    timestamp: new Date(),
    data,
  };
}

function assignIfPresent(target: Record<string, any>, key: string, value: any): void {
  if (value == null) {
    return;
  }
  const asString = typeof value === 'string' ? value.trim() : String(value).trim();
  if (asString === '') {
    return;
  }
  target[key] = typeof value === 'string' ? asString : value;
}

/**
 * LPR Approach A: map unredeemable Way lane payload into create-order-request for staff sale.
 * Requires lpr_entry_id (lane) — order-request lookup keeps the underlying form instead.
 *
 * Intentionally does not spread the raw Way payload: terminal Way `event` / purchase fields would
 * make notification-modal hydration remap away from create-order and show a blank body.
 */
export function buildCreateOrderRequestFromUnredeemable(
  data: WayPurchaseNotificationData | Record<string, any> | null | undefined
): NotificationData | null {
  const row = readRecord(data);
  if (!row) {
    return null;
  }

  const lprEntryId = pickString(row['lpr_entry_id'], row['lprEntryId'], row['id']);
  const locationId = pickString(row['location_id'], row['locationId']);
  if (!lprEntryId || !locationId) {
    return null;
  }

  const purchase = readRecord(row['purchase']);
  const plate = pickString(
    row['plate'],
    row['plateNumber'],
    purchase?.['plate_number'],
    row['vehicle_license_plate']
  );
  const state = pickString(row['state'], row['vehicle_state']);
  const customerId = pickString(row['customer_id'], row['customerId']);
  const vehicleId = pickString(row['vehicle_id'], row['vehicleId']);
  const reason = pickString(row['reason'], purchase?.['unredeemable_reason'], row['message']);

  const payload: Record<string, any> = {
    event: 'create-order-request',
    location_id: locationId,
    lpr_entry_id: lprEntryId,
    message: 'No redeemable Way purchase. Prompt user to create an order.',
    way_unredeemable_handoff: true,
  };

  if (plate) {
    payload['plate'] = plate;
    payload['plateNumber'] = plate;
    payload['vehicle_license_plate'] = plate;
  }
  if (state) {
    payload['state'] = state;
  }
  if (customerId) {
    payload['customer_id'] = customerId;
    payload['customerId'] = customerId;
  }
  if (vehicleId) {
    payload['vehicle_id'] = vehicleId;
    payload['vehicleId'] = vehicleId;
  }
  if (reason) {
    payload['way_unredeemable_reason'] = reason;
  }

  // Keep staff user_id separate from customers.id — never map userId onto customerId.
  assignIfPresent(payload, 'user_id', row['user_id'] ?? row['userId']);
  assignIfPresent(payload, 'userId', row['userId'] ?? row['user_id']);
  assignIfPresent(payload, 'branch', row['branch']);
  assignIfPresent(payload, 'triggered_timestamp', row['triggered_timestamp']);
  assignIfPresent(payload, 'customer_vendor_location_id', row['way_vendor_location_id']);
  assignIfPresent(payload, 'customer_first_name', row['customer_first_name']);
  assignIfPresent(payload, 'customer_last_name', row['customer_last_name']);
  assignIfPresent(payload, 'customer_email', row['customer_email']);
  assignIfPresent(payload, 'customer_contact', row['customer_contact']);
  assignIfPresent(payload, 'stripe_customer_id', row['stripe_customer_id']);
  assignIfPresent(payload, 'vehicle_make', row['vehicle_make']);
  assignIfPresent(payload, 'vehicle_model', row['vehicle_model']);
  assignIfPresent(payload, 'vehicle_year', row['vehicle_year']);
  assignIfPresent(payload, 'vehicle_vin', row['vehicle_vin']);
  assignIfPresent(payload, 'image_s3_url', row['image_s3_url']);
  assignIfPresent(payload, 'plate_image_s3_url', row['plate_image_s3_url']);
  assignIfPresent(payload, 'roi_id', row['roi_id'] ?? row['roiID'] ?? row['roiId']);

  return {
    type: 'create-order-request',
    message: 'Order request created',
    timestamp: new Date(),
    data: payload,
  };
}

/** True when selection has no usable shop/Way option (cancel should hand off to create-order). */
export function selectionHasNoUsablePurchaseOptions(
  data: WayPurchaseNotificationData | Record<string, any> | null | undefined
): boolean {
  const row = readRecord(data);
  const options = row?.['purchase_options'];
  if (!Array.isArray(options) || !options.length) {
    return true;
  }

  return !options.some(item => {
    const option = readRecord(item);
    if (!option) {
      return false;
    }
    if (option['source'] === 'shop') {
      return (
        String(option['order_status'] ?? '')
          .trim()
          .toLowerCase() !== 'cancelled'
      );
    }
    if (option['source'] === 'way') {
      const purchase = readRecord(option['purchase']);
      if (!purchase) {
        return false;
      }
      return isRedeemableWayPurchase(purchase as WayNormalizedPurchase);
    }
    return false;
  });
}

/** Order-request Way redeem: always open order-success (single wash and membership). */
export function buildOrderSuccessNotificationFromConfirmWayResponse(
  response: any
): NotificationData | null {
  const envelope = readRecord(response);
  const data = readRecord(envelope?.['data']);
  if (!data) {
    return null;
  }

  const message =
    pickString(data['message'], envelope?.['message']) ||
    NotificationUtils.getMessageForEventType('order-success');

  return {
    type: 'order-success',
    message,
    timestamp: new Date(),
    data,
  };
}
